import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { queryKeys } from "@/queryKeys";
import api from "@/services/api";

import { agentClient } from "../agentClient";
import { AGENT_CONTEXTS } from "../apps";
import { appLink } from "../contexts/registry";
import type { AgentState } from "../store/agentSlice";
import { useIsPhone } from "../chat/PhoneSheet";
import { selectChoice } from "../store/selectors";
import { createBridge, FRAME_HEIGHT, type Platform } from "./mcpBridge";

async function fetchApprovalView(): Promise<string> {
  const { data } = await api.get<string>("/agent/apps/approval/", { responseType: "text" });
  return data;
}

export function useApprovalViewHtml() {
  return useQuery({
    queryKey: queryKeys.agentApprovalView(),
    queryFn: fetchApprovalView,
    staleTime: Infinity,
    // One quick retry, then the card says it didn't load (the default's backoff took ~12 s).
    retry: 1,
    retryDelay: 800,
  });
}

/**
 * A view's ui/open-link, for the frame's `openLink`: a link to the choice's own subject navigates
 * in-app to its canonical path; anything else is refused, and nothing opens a window. Stable, so
 * the frame's bridge outlives route changes and a subject arriving later.
 */
export function useInAppLinks(subjectRef: string | null): (url: string) => boolean {
  const navigate = useNavigate();
  const latest = useRef({ navigate, subjectRef });
  latest.current = { navigate, subjectRef };
  return useCallback((url: string) => {
    const link = appLink(AGENT_CONTEXTS, url);
    if (!link || link.ref !== latest.current.subjectRef) return false;
    latest.current.navigate(link.path);
    return true;
  }, []);
}

/**
 * The approval view (`ui://steddy/approval`) in our own sandboxed frame: `allow-scripts` without
 * `allow-same-origin` gives it an opaque origin (no cookies, storage or DOM of ours), and nothing
 * loads from any other domain. It talks only through the bridge, and only to decide its own choice
 * and to ask `openLink` (stable; useInAppLinks) to open a link. Without it, every link is refused.
 */
export function McpAppFrame({
  choiceRef,
  title = "Approval",
  openLink,
}: {
  choiceRef: string;
  title?: string;
  openLink?: (url: string) => boolean;
}) {
  const { data: html, isError, isFetching, refetch } = useApprovalViewHtml();
  // Try again shows the spinner while it retries.
  const failed = isError && !isFetching;
  const choice = useSelector((state: { agent: AgentState }) => selectChoice(state, choiceRef));
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number>(FRAME_HEIGHT.min);
  // Read by the bridge when the view asks, so the bridge itself outlives a layout change.
  const phone = useIsPhone();
  const platform = useRef<Platform>(phone ? "mobile" : "web");

  const bridge = useMemo(
    () =>
      createBridge({
        post: (message) => frame.current?.contentWindow?.postMessage(message, "*"),
        decide: (option, rows, shown) =>
          agentClient.decide(
            choiceRef,
            option,
            undefined,
            rows,
            shown === undefined ? undefined : { shown }
          ),
        setHeight,
        openLink,
        locale: navigator.language,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        platform: () => platform.current,
      }),
    [choiceRef, openLink]
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The frame's origin is the string "null": its window is the only proof of who sent it.
      if (frame.current && event.source === frame.current.contentWindow) bridge.receive(event.data);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [bridge]);

  useEffect(() => bridge.choiceChanged(choice), [bridge, choice]);

  useEffect(() => {
    const next = phone ? "mobile" : "web";
    if (platform.current === next) return;
    platform.current = next;
    bridge.platformChanged();
  }, [bridge, phone]);

  // The card's place in the thread is kept while its view loads, and a failed load says so.
  if (!html) {
    return (
      <div
        aria-busy={!failed}
        className="flex min-h-[60px] items-center gap-2 rounded-assistant border px-3.5 text-xs max-sm:rounded-none max-sm:border-x-0"
      >
        {failed ? (
          <>
            <span role="alert" className="text-destructive">
              The approval didn't load.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto max-sm:h-10"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </>
        ) : (
          <Loader2
            className="h-4 w-4 text-muted-foreground motion-safe:animate-spin"
            aria-label="Loading"
          />
        )}
      </div>
    );
  }
  return (
    <iframe
      ref={frame}
      title={title}
      sandbox="allow-scripts"
      srcDoc={html}
      className="block w-full border-0"
      style={{ height }}
    />
  );
}
